import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowUpRight, Loader2, RefreshCw, Send, Trash2, Unlink } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { CopyField } from '../../../components/design-system/common/CopyField';
import { ConfirmActionDialog } from '../../../components/design-system/admin/ConfirmActionDialog';
import { DESTRUCTIVE_OUTLINE } from '../../../components/design-system/admin/styles';
import { ArdiChannel, ChannelMessage, useArdiApi } from '../../../common/hooks/use-ardi-api.hook';
import { NotificationPreferenceRow, useNotificationsApi } from '../../../common/hooks/use-notifications-api.hook';
import { readApiError } from '../../../common/utils/api-error';
import { CATEGORY_ORDER, categoryKey } from '../../notifications/notification-presentation';
import { RelativeTime } from '../../../components/design-system/admin/RelativeTime';
import { channelPhase } from './channel-presentation';

const Section: React.FC<{ title: React.ReactNode; description?: React.ReactNode; children: React.ReactNode; className?: string }> = ({ title, description, children, className }) => (
  <section className={cn('flex flex-col gap-3', className)}>
    <div>
      <h3 className="text-sm font-medium">{title}</h3>
      {description && <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>}
    </div>
    {children}
  </section>
);

/** Minutes left on a pairing code, ticking. */
function useMinutesLeft(until?: string | null) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!until) return;
    const id = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(id);
  }, [until]);
  return until ? Math.max(0, Math.ceil((new Date(until).getTime() - now) / 60_000)) : 0;
}

/**
 * A connected channel: linking chats to it, what Ardi may do there, what it
 * says there on its own, and the record of what went in and out.
 */
export function ChannelManage({
  channel,
  onChange,
  onRemoved,
}: {
  channel: ArdiChannel;
  onChange: (c: ArdiChannel) => void;
  onRemoved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const api = useArdiApi();
  const notificationsApi = useNotificationsApi();
  const [types, setTypes] = useState<NotificationPreferenceRow[] | null>(null);
  const [messages, setMessages] = useState<ChannelMessage[] | null>(null);
  const [testing, setTesting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [outbound, setOutbound] = useState<string>(channel.info?.outboundUrl ?? '');
  const [outboundError, setOutboundError] = useState<string | null>(null);
  const phase = channelPhase(channel);
  const minutes = useMinutesLeft(channel.pairing?.expiresAt);
  const isWebhook = channel.kind === 'webhook';

  useEffect(() => {
    notificationsApi.getPreferences().then((p) => setTypes(p.types)).catch(() => setTypes([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    api.channelMessages(channel.uuid).then(setMessages).catch(() => setMessages([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel.uuid, channel.stats.messagesIn, channel.stats.messagesOut]);

  const save = async (patch: Parameters<typeof api.updateChannel>[1]) => {
    try {
      onChange(await api.updateChannel(channel.uuid, patch));
      return true;
    } catch (err) {
      const body = await readApiError(err);
      toast.error(body.message ? String(body.message) : t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
      return false;
    }
  };

  const toggleType = (type: string, on: boolean) =>
    save({ notifyTypes: on ? [...new Set([...channel.notifyTypes, type])] : channel.notifyTypes.filter((x) => x !== type) });

  const newCode = async () => {
    try {
      onChange(await api.newPairingCode(channel.uuid));
    } catch {
      toast.error(t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const r = await api.testChannel(channel.uuid);
      if (r.sent) toast.success(t('ARDI.CHANNELS.MANAGE.TEST_SENT', { count: r.sent }));
      else toast.error(t('ARDI.CHANNELS.MANAGE.TEST_FAILED'));
      api.channelMessages(channel.uuid).then(setMessages).catch(() => undefined);
    } catch (err) {
      const body = await readApiError(err);
      toast.error(body.message ? String(body.message) : t('ARDI.CHANNELS.MANAGE.TEST_FAILED'));
    } finally {
      setTesting(false);
    }
  };

  const unlink = async (externalId: string) => {
    try {
      onChange(await api.unlinkChat(channel.uuid, externalId));
    } catch {
      toast.error(t('ARDI.NOTIFICATIONS.SAVE_FAILED'));
    }
  };

  const remove = async () => {
    await api.removeChannel(channel.uuid);
    onRemoved();
  };

  const saveOutbound = async () => {
    setOutboundError(null);
    const ok = await save({ outboundUrl: outbound.trim() || null });
    if (!ok) setOutboundError(t('ARDI.CHANNELS.WEBHOOK.OUTBOUND_INVALID') as string);
    else toast.success(t('ARDI.CHANNELS.MANAGE.SAVED'));
  };

  const grouped = useMemo(() => {
    if (!types) return [];
    return CATEGORY_ORDER.map((category) => ({ category, rows: types.filter((r) => r.category === category) })).filter((g) => g.rows.length);
  }, [types]);

  const curl = `curl -X POST '${channel.setup.inboundUrl}' \\\n  -H 'Authorization: Bearer ${channel.setup.token ?? '<token>'}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"message": "How many leads did we get this week?", "conversationId": "my-automation"}'`;

  return (
    <div className="flex flex-col gap-8" data-testid="channel-manage">
      {phase === 'error' && (
        <Alert variant="destructive">
          <AlertDescription className="text-xs">
            {channel.needsReconnect ? t('ARDI.CHANNELS.MANAGE.NEEDS_RECONNECT') : t('ARDI.CHANNELS.MANAGE.ERROR', { error: channel.lastError ?? '' })}
          </AlertDescription>
        </Alert>
      )}

      {/* ── Linking chats ─────────────────────────────────────────────── */}
      {!isWebhook && (
        <Section title={t('ARDI.CHANNELS.MANAGE.LINK_TITLE')} description={t(`ARDI.CHANNELS.MANAGE.LINK_DESC_${channel.kind.toUpperCase()}`)}>
          {channel.pairing ? (
            <div className="flex flex-col items-start gap-3 rounded-xl border p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-mono text-3xl tracking-[0.3em] tabular-nums" data-testid="pairing-code">{channel.pairing.code}</p>
                <p className="mt-1 text-xs text-muted-foreground">{t('ARDI.CHANNELS.MANAGE.CODE_EXPIRES', { count: minutes })}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {channel.pairing.deepLink && (
                  <Button variant="outline" size="sm" asChild>
                    <a href={channel.pairing.deepLink} target="_blank" rel="noreferrer">
                      {t(`ARDI.CHANNELS.MANAGE.OPEN_${channel.kind.toUpperCase()}`)}
                      <ArrowUpRight data-icon="inline-end" />
                    </a>
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={newCode} className="text-muted-foreground">
                  <RefreshCw />
                  {t('ARDI.CHANNELS.MANAGE.NEW_CODE')}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" size="sm" onClick={newCode} className="self-start">
              {t('ARDI.CHANNELS.MANAGE.GET_CODE')}
            </Button>
          )}

          {channel.links.length > 0 ? (
            <ul className="divide-y rounded-xl border" data-testid="linked-chats">
              {channel.links.map((l) => (
                <li key={l.externalId} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate text-sm">{l.label || l.externalId}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('ARDI.CHANNELS.MANAGE.LINKED')} <RelativeTime value={l.linkedAt} />
                      {l.lastMessageAt && <> · {t('ARDI.CHANNELS.MANAGE.LAST_MESSAGE')} <RelativeTime value={l.lastMessageAt} /></>}
                    </p>
                  </div>
                  <Button variant="ghost" size="icon-sm" onClick={() => unlink(l.externalId)} aria-label={t('ARDI.CHANNELS.MANAGE.UNLINK', { name: l.label }) as string}>
                    <Unlink />
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-muted-foreground">{t('ARDI.CHANNELS.MANAGE.NO_CHATS')}</p>
          )}
        </Section>
      )}

      {/* ── Webhook endpoint ──────────────────────────────────────────── */}
      {isWebhook && (
        <Section title={t('ARDI.CHANNELS.WEBHOOK.INBOUND_TITLE')} description={t('ARDI.CHANNELS.WEBHOOK.INBOUND_DESC')}>
          <CopyField label={t('ARDI.CHANNELS.WEBHOOK.URL')} value={channel.setup.inboundUrl ?? ''} testId="webhook-url" />
          {channel.setup.token && <CopyField label={t('ARDI.CHANNELS.WEBHOOK.TOKEN')} value={channel.setup.token} secret />}
          <CopyField label={t('ARDI.CHANNELS.WEBHOOK.EXAMPLE')} value={curl} multiline />
          <p className="text-xs leading-relaxed text-muted-foreground">{t('ARDI.CHANNELS.WEBHOOK.RESPONSE')}</p>
          <CopyField label={t('ARDI.CHANNELS.WEBHOOK.DECIDE')} value={`{"approve": "<actionId>", "conversationId": "my-automation"}`} />
        </Section>
      )}
      {isWebhook && (
        <Section title={t('ARDI.CHANNELS.WEBHOOK.OUTBOUND_TITLE')} description={t('ARDI.CHANNELS.WEBHOOK.OUTBOUND_DESC')}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`outbound-${channel.uuid}`} className="text-xs font-medium text-muted-foreground">{t('ARDI.CHANNELS.WEBHOOK.OUTBOUND_URL')}</Label>
            <div className="flex gap-2">
              <Input id={`outbound-${channel.uuid}`} value={outbound} onChange={(e) => setOutbound(e.target.value)} placeholder="https://hooks.zapier.com/…" className="font-mono text-xs" />
              <Button variant="outline" onClick={saveOutbound}>{t('ARDI.CHANNELS.MANAGE.SAVE')}</Button>
            </div>
            {outboundError && <p className="text-xs text-destructive">{outboundError}</p>}
            <p className="text-xs text-muted-foreground">{t('ARDI.CHANNELS.WEBHOOK.SIGNATURE')}</p>
          </div>
        </Section>
      )}

      {/* ── What Ardi may do here ─────────────────────────────────────── */}
      <Section title={t('ARDI.CHANNELS.MANAGE.BEHAVIOUR_TITLE')}>
        <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
          <div>
            <Label htmlFor={`conv-${channel.uuid}`} className="text-sm font-normal">{t('ARDI.CHANNELS.MANAGE.CONVERSATIONS')}</Label>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t('ARDI.CHANNELS.MANAGE.CONVERSATIONS_DESC')}</p>
          </div>
          <Switch id={`conv-${channel.uuid}`} checked={channel.conversational} onCheckedChange={(v) => save({ conversational: v })} />
        </div>
        {/* Approving from the phone is the point of a channel; off for a group where not everyone should decide. */}
        <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
          <div>
            <Label htmlFor={`approve-${channel.uuid}`} className="text-sm font-normal">{t('ARDI.CHANNELS.MANAGE.APPROVALS')}</Label>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
              {t(isWebhook ? 'ARDI.CHANNELS.MANAGE.APPROVALS_DESC_WEBHOOK' : 'ARDI.CHANNELS.MANAGE.APPROVALS_DESC')}
            </p>
          </div>
          <Switch id={`approve-${channel.uuid}`} checked={channel.allowApprovals !== false} onCheckedChange={(v) => save({ allowApprovals: v })} />
        </div>
        {/* A Slack app made before buttons existed needs Interactivity switched on in Slack. */}
        {channel.kind === 'slack' && channel.allowApprovals !== false && (
          <div className="flex flex-col gap-3 rounded-xl border p-4" data-testid="slack-interactivity">
            <div className="flex items-start justify-between gap-4">
              <div>
                <Label htmlFor={`interactive-${channel.uuid}`} className="text-sm font-normal">{t('ARDI.CHANNELS.MANAGE.SLACK_BUTTONS')}</Label>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t('ARDI.CHANNELS.MANAGE.SLACK_BUTTONS_DESC')}</p>
              </div>
              <Switch id={`interactive-${channel.uuid}`} checked={!!channel.info?.interactive} onCheckedChange={(v) => save({ interactive: v })} />
            </div>
            {!channel.info?.interactive && channel.setup.inboundUrl && <CopyField label={t('ARDI.CHANNELS.MANAGE.SLACK_REQUEST_URL')} value={channel.setup.inboundUrl} />}
          </div>
        )}
      </Section>

      <Section title={t('ARDI.CHANNELS.MANAGE.NOTIFY_TITLE')} description={t('ARDI.CHANNELS.MANAGE.NOTIFY_DESC')}>
        {!types ? null : (
          <div className="flex flex-col gap-4" data-testid="channel-notify-types">
            {grouped.map((g) => (
              <div key={g.category} className="flex flex-col gap-2">
                <p className="text-xs font-medium text-muted-foreground">{t(categoryKey(g.category))}</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {g.rows.map((row) => {
                    const id = `nt-${channel.uuid}-${row.type}`;
                    return (
                      <label key={row.type} htmlFor={id} className="flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 hover:bg-accent">
                        <Checkbox id={id} checked={channel.notifyTypes.includes(row.type)} onCheckedChange={(v) => toggleType(row.type, v === true)} className="mt-0.5" />
                        <span className="min-w-0">
                          <span className="block text-sm leading-snug">{row.label}</span>
                          <span className="block text-xs leading-snug text-muted-foreground">{row.about}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
        <Button variant="outline" size="sm" onClick={test} disabled={testing} className="self-start">
          {testing ? <Loader2 className="animate-spin" /> : <Send />}
          {t('ARDI.CHANNELS.MANAGE.TEST')}
        </Button>
      </Section>

      {/* ── What went in and out ──────────────────────────────────────── */}
      <Section title={t('ARDI.CHANNELS.MANAGE.LOG_TITLE')} description={t('ARDI.CHANNELS.MANAGE.LOG_DESC')}>
        {!messages?.length ? (
          <p className="text-xs text-muted-foreground">{t('ARDI.CHANNELS.MANAGE.LOG_EMPTY')}</p>
        ) : (
          <ul className="divide-y rounded-xl border" data-testid="channel-log">
            {messages.map((m) => (
              <li key={m._id} className="flex items-start gap-3 px-4 py-2.5">
                <span className={cn('mt-0.5 w-10 shrink-0 text-[11px] font-medium uppercase', m.direction === 'in' ? 'text-foreground' : 'text-muted-foreground')}>
                  {t(m.direction === 'in' ? 'ARDI.CHANNELS.MANAGE.IN' : 'ARDI.CHANNELS.MANAGE.OUT')}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="line-clamp-2 text-xs leading-relaxed">{m.preview || '—'}</p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    <RelativeTime value={m.createdAt} />
                    {m.reason && <> · {t(`ARDI.CHANNELS.REASON.${m.reason.toUpperCase().replace(/-/g, '_')}`, m.reason)}</>}
                    {m.status !== 'ok' && <span className={cn(m.status === 'failed' && 'text-destructive')}> · {t(`ARDI.CHANNELS.MESSAGE_STATUS.${m.status.toUpperCase()}`)}</span>}
                  </p>
                  {m.error && <p className="mt-0.5 truncate text-[11px] text-destructive">{m.error}</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title={t('ARDI.CHANNELS.MANAGE.DANGER_TITLE')} description={t('ARDI.CHANNELS.MANAGE.DANGER_DESC')}>
        <Button variant="outline" size="sm" onClick={() => setConfirmRemove(true)} className={cn('self-start', DESTRUCTIVE_OUTLINE)}>
          <Trash2 />
          {t('ARDI.CHANNELS.MANAGE.DISCONNECT')}
        </Button>
      </Section>

      <ConfirmActionDialog
        open={confirmRemove}
        onOpenChange={setConfirmRemove}
        title={t('ARDI.CHANNELS.MANAGE.DISCONNECT_CONFIRM_TITLE', { name: channel.name }) as string}
        description={t('ARDI.CHANNELS.MANAGE.DISCONNECT_CONFIRM_DESC') as string}
        confirmLabel={t('ARDI.CHANNELS.MANAGE.DISCONNECT') as string}
        destructive
        onConfirm={remove}
      />
    </div>
  );
}
